import { config } from '@/lib/env-helpers';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { Message, MessageType, createClient, stringifyMessage } from 'graphql-ws';
import { createHeaders } from './auth-link';
import { ApolloLink } from '@apollo/client';
import { cache } from './cache';
import refreshToken from './refresh-token';
import { WORKSPACE_AUTH_ME } from '@/graphql/query.graphql';
import { workspaceAuthMeV2Query } from '@/types/generated/graphql';
import { getProvider } from '@/lib/callProviders/provider-registry';
import { useDeviceStore } from '@/modules/phone/store/device.store';

type Socket = { readyState: unknown; close: (n: number, s: string) => void; send: (_: string) => void };
let activeSocket: Socket | undefined, timedOut: NodeJS.Timeout;

function onPing(received: boolean) {
  const workspaceDetails: workspaceAuthMeV2Query | null = cache.readQuery({ query: WORKSPACE_AUTH_ME });
  const { currentUserWorkspace, id: workspaceId } = workspaceDetails?.workspaceAuthMeV2?.data || {};
  const { device, call } = useDeviceStore.getState();
  const isBusy = device ? call !== null || !!getProvider(device.provider)?.isDeviceBusy(device) : undefined;
  const pingPayload = {
    userWorkspaceId: currentUserWorkspace?.id,
    workspaceId,
    isBusy,
  };

  if (!received)
    // sent
    timedOut = setTimeout(() => {
      if (activeSocket?.readyState === WebSocket.OPEN) activeSocket?.close(4408, 'Request Timeout');
    }, 5_000); // wait 5 seconds for the pong and then close the connection

  activeSocket?.send(
    stringifyMessage({
      type: MessageType.Ping,
      payload: pingPayload,
    })
  );
}

function onPong(received: boolean) {
  if (received) clearTimeout(timedOut); // pong is received, clear connection close timeout
}

function onConnected(socket: unknown) {
  console.info('Active connection established with GraphQL WebSocket server');
  activeSocket = socket as Socket;
}

let refreshingToken = false;

function onMessage(message: Message) {
  const hasUnauthenticatedError =
    message.type === 'next' &&
    'payload' in message &&
    message?.payload?.errors?.some((error) => error.extensions?.code === 'UNAUTHENTICATED');

  if (hasUnauthenticatedError && !refreshingToken) {
    refreshingToken = true;
    refreshToken().finally(() => {
      refreshingToken = false;
      // Normal closure with new access token already set in cookies; triggers auto-reconnect
      activeSocket?.close(1000, 'Authentication expired');
    });
  }
}

const clientOptions: Parameters<typeof createClient>[0] = {
  retryAttempts: 5,
  keepAlive: 10_000,
  connectionParams: createHeaders,
  url: config.SUBSCRIPTION_ENDPOINT,
  on: {
    connected: onConnected,
    message: onMessage,
    ping: onPing,
    pong: onPong,
  },
};

export const subscriptionLink = new GraphQLWsLink(createClient(clientOptions)) as unknown as ApolloLink;
